import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ReactNode } from 'react';

/**
 * Kategoriska färger för ledningsrapportens widgets.
 *
 * Ordningen är inte kosmetisk: den är vald och validerad så att intilliggande par går att skilja
 * åt även vid färgblindhet. Byt inte plats på dem och lägg inte till en nionde — då faller
 * garantin. Fler serier än så viks in i "Övrigt".
 */
export const MANAGEMENT_COLORS = [
  '#2a78d6', // blå
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // gul
  '#e87ba4', // magenta
  '#008300', // grön
  '#4a3aa7', // violett
  '#e34948', // röd
];

export const colorAt = (index: number) =>
  MANAGEMENT_COLORS[index % MANAGEMENT_COLORS.length];

/** Svenska tal med tusentalsavgränsare. */
export const nf = (value: number, decimals = 0) =>
  value.toLocaleString('sv-SE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

export const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? '–' : `${nf(value, 1)} %`;

/**
 * Timmar, med dygn i parentes så snart talet passerar ett dygn. En ledtid på 104 h säger
 * ingenting förrän läsaren översatt den till "drygt fyra dygn", och den översättningen ska
 * inte behöva göras i huvudet mitt i ett ledningsmöte.
 */
export const hrs = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '–';
  if (value < 24) return `${nf(value, 1)} h`;
  return `${nf(value, 1)} h (${nf(value / 24, 1)} dygn)`;
};

/** Timmar som h:mm — "1:45" läses snabbare än "1,8 h" när det jämförs med ett estimat. */
export const hm = (hours: number | null | undefined) => {
  if (hours === null || hours === undefined) return '–';
  const minutes = Math.round(hours * 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
};

/**
 * Gemensamt tomtillstånd. Ett för långt intervall är inte samma sak som ingen data, och
 * en pågående laddning är inte heller det — att visa "ingen data" i alla tre lägena får
 * läsaren att dra fel slutsats.
 */
export const WidgetState = ({
  isLoading,
  isDateRangeTooLong,
  isEmpty,
  children,
}: {
  isLoading: boolean;
  isDateRangeTooLong: boolean;
  isEmpty: boolean;
  children: ReactNode;
}) => {
  if (isDateRangeTooLong) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <p>Datumintervallet är för långt för att visa data. Välj en kortare period.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <FontAwesomeIcon icon={faSpinner} spin />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <p>Ingen data att visa för vald filtrering.</p>
      </div>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

/**
 * Kontrollsiffror — de tal som gör luckorna i underlaget synliga. Visas dämpat under datan,
 * eftersom de är metadata om hur pålitlig siffran är, inte siffran i sig.
 */
export const ControlFigures = ({ items }: { items: (string | null)[] }) => {
  const visible = items.filter((i): i is string => !!i);
  if (visible.length === 0) return null;

  return (
    <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
      {visible.join(' · ')}
    </p>
  );
};

export const thStyle: React.CSSProperties = {
  padding: '8px',
  textAlign: 'left',
  borderBottom: '2px solid #ddd',
};

export const thNumStyle: React.CSSProperties = {
  ...thStyle,
  textAlign: 'right',
};

export const tdStyle: React.CSSProperties = { padding: '8px' };

export const tdNumStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

export const totalRowStyle: React.CSSProperties = {
  borderTop: '2px solid #ddd',
  fontWeight: 'bold',
};

export const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};
